import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/tokenService";

const statusColors = {
  Pending: "bg-warning text-dark",
  Supervisor: "bg-info text-dark",
  Approved: "bg-success",
  Rejected: "bg-danger",
};

const ClientLeaveManagement = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    statusCounts: {},
    typeCounts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadLeaveData = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/client/leave-management`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLeaveRequests(response.data.leaveRequests || []);
        setSummary(
          response.data.summary || {
            total: 0,
            statusCounts: {},
            typeCounts: {},
          }
        );
      } catch (err) {
        console.error("Failed to load leave data:", err);
        setError("Unable to load leave data right now.");
        toast.error("Could not fetch leave management data.");
      } finally {
        setLoading(false);
      }
    };

    loadLeaveData();
  }, []);

  const renderSummaryCard = (label, value) => (
    <div className="col-md-3 mb-2">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <p className="text-muted mb-1">{label}</p>
          <h5 className="mb-0">{value}</h5>
        </div>
      </div>
    </div>
  );

  const formatDateRange = (from, to) => {
    const fromDate = new Date(from).toLocaleDateString();
    const toDate = new Date(to).toLocaleDateString();
    return `${fromDate} → ${toDate}`;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Loading leave records...</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Leave Management</h4>
          <p className="mb-0 text-muted">
            Reviews and balances for {summary.total} requests
          </p>
        </div>
      </div>
      {error && <div className="alert alert-warning">{error}</div>}
      <div className="row mb-3">
        {renderSummaryCard("Total requests", summary.total)}
        {renderSummaryCard("Pending", summary.statusCounts?.Pending || 0)}
        {renderSummaryCard("Approved", summary.statusCounts?.Approved || 0)}
        {renderSummaryCard("Rejected", summary.statusCounts?.Rejected || 0)}
      </div>
      <div className="row mb-3">
        {(summary.typeCounts || {}).Casual && (
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <p className="text-muted mb-1">Casual leaves</p>
                <h5 className="mb-0">{summary.typeCounts.Casual || 0}</h5>
              </div>
            </div>
          </div>
        )}
        {(summary.typeCounts || {}).Earned && (
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <p className="text-muted mb-1">Earned leaves</p>
                <h5 className="mb-0">{summary.typeCounts.Earned || 0}</h5>
              </div>
            </div>
          </div>
        )}
        {(summary.typeCounts || {}).Medical && (
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <p className="text-muted mb-1" style={{ whiteSpace: "nowrap" }}>
                  Medical leaves
                </p>
                <h5 className="mb-0">{summary.typeCounts.Medical || 0}</h5>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="table-responsive">
        <table className="table table-striped align-middle">
          <thead className="table-light">
        <tr>
          <th>Employee ID</th>
          <th>Employee</th>
          <th>Leave Period</th>
          <th>Reason</th>
          <th>Type</th>
          <th>Status</th>
        </tr>
          </thead>
          <tbody>
            {leaveRequests.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No leaves applied for your clients yet.
                </td>
              </tr>
            ) : (
              leaveRequests.map((request) => (
              <tr key={request._id}>
                <td className="text-start">
                  {request.user_id?.employeeId
                    ? `CMPL${request.user_id.employeeId}`
                    : "-"}
                </td>
                <td>
                  <strong>{request.user_id?.full_Name || "N/A"}</strong>
                  <div className="text-muted small">
                    {request.user_id?.email || request.user_id?.contact || "—"}
                  </div>
                </td>
                <td>{formatDateRange(request.from, request.to)}</td>
                <td>{request.reason}</td>
                <td>
                  {request.leaveType} / {request.leaveSubType}
                </td>
                <td>
                  <span
                    className={`badge ${
                      statusColors[request.status] || "bg-secondary"
                    }`}
                  >
                    {request.status}
                  </span>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientLeaveManagement;
